import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { Link } from "react-router-dom";

// Real WVE behind the scenes photos from actual wedding days
const btsPosts = [
  {
    image: "https://customer-assets.emergentagent.com/job_essex-wedding-films/artifacts/x688lp10_7fcf9bde-226f-46a2-8ad1-72631bf5d44e.jpeg",
    caption: "Capturing the couple in the walled garden. Spring weddings in Essex are something else.",
  },
  {
    image: "https://customer-assets.emergentagent.com/job_essex-wedding-films/artifacts/1bd4nto7_536a87c3-d217-4dbd-9171-4b424e7bc63c.jpeg",
    caption: "Photographer and videographer working together under the festoon lights. This is what teamwork looks like.",
  },
  {
    image: "https://customer-assets.emergentagent.com/job_essex-wedding-films/artifacts/qvohih3a_68bf6f06-bd19-450a-afb2-11eee303d2fa.jpeg",
    caption: "Golden hour on a country lane. These are the moments we live for.",
  },
  {
    image: "https://customer-assets.emergentagent.com/job_essex-wedding-films/artifacts/2veqyfgu_12f45291-c95d-4ce0-93a4-7b7945308abe.jpeg",
    caption: "Two cameras, one frame. Shooting through the lion gates as the couple walk towards us.",
  },
  {
    image: "https://customer-assets.emergentagent.com/job_essex-wedding-films/artifacts/pveejrjl_B277DC91-581A-4E1E-920E-E4A0E6201272.jpeg",
    caption: "Inside the manor hallway. Photographer and videographer side by side, never in each other's way.",
  },
  {
    image: "/images/IMG_1569.jpg",
    caption: "Setting up the shot before the first look. Every angle matters.",
  },
  {
    image: "https://customer-assets.emergentagent.com/job_essex-wedding-films/artifacts/ssb395rm_b14a407c-9290-4bb7-b11d-628b1afe5c78.jpeg",
    caption: "Behind the lens on a beautiful Essex summer wedding. No two days are ever the same.",
  },
  {
    image: "https://customer-assets.emergentagent.com/job_essex-wedding-films/artifacts/hs9neeqa_0a6a5f9f-7ac3-46ea-b486-ecba04622523.jpeg",
    caption: "Working the room during the speeches. These reactions are everything.",
  },
  {
    image: "/images/IMG_1014.jpg",
    caption: "Gear ready, team briefed, doors about to open. This is the calm before the magic.",
  },
  {
    image: "https://customer-assets.emergentagent.com/job_essex-wedding-films/artifacts/h3lijwmg_6BE21FBC-82E9-45CE-A717-791E1271F9FB.jpeg",
    caption: "The whole crew on location. Photo and video working as one team.",
  },
];

export function InstagramSection({ showAll = false }) {
  const posts = showAll ? btsPosts : btsPosts.slice(0, 8);

  return (
    <section data-testid="instagram-section" className="bg-[#0A0908] py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10"
        >
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Instagram className="w-4 h-4 text-[#C2A878]" />
              <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans">
                Behind the Scenes
              </p>
            </div>
            <h2
              data-testid="instagram-heading"
              className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#F5F5F0]"
            >
              What it's actually like on the day
            </h2>
            <p className="mt-3 text-sm text-[#A19D94] font-sans leading-relaxed max-w-lg">
              The real deal. Just us doing what we love at some of the best
              venues in Essex.
            </p>
          </div>

          <a
            href="https://instagram.com/WeddingVideoEssex"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-link"
            className="inline-flex items-center gap-2 text-sm text-[#C2A878] hover:text-[#d4bc92] transition-colors font-sans shrink-0"
          >
            <Instagram size={16} />
            @WeddingVideoEssex
          </a>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {posts.map((post, i) => (
            <motion.a
              key={i}
              href="https://instagram.com/WeddingVideoEssex"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`instagram-post-${i}`}
              className="group relative aspect-square overflow-hidden border border-white/[0.06]"
            >
              <img
                src={post.image}
                alt={post.caption}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-300 flex items-end p-3 md:p-4">
                <p className="text-xs text-white font-sans opacity-0 group-hover:opacity-100 transition-opacity duration-300 leading-relaxed">
                  {post.caption}
                </p>
              </div>
            </motion.a>
          ))}
        </div>

        {!showAll && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8 text-center"
          >
            <Link
              to="/real-weddings"
              data-testid="instagram-see-more-link"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#C2A878]/30 text-sm text-[#C2A878] font-sans tracking-wide hover:bg-[#C2A878]/10 transition-all duration-300"
            >
              See more behind the scenes
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
}

export { btsPosts };
